import { useController, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { NoxusFormLabel } from "./NoxusFormLabel";

interface CardSelectItem {
  value: string;
  label: string;
  description?: string | null;
  image?: string | null;
}

interface CardSelectFieldProps {
  name: string;
  label: string;
  items: CardSelectItem[];
  defaultValue?: string;
  tooltip?: string;
  isDisabled?: boolean;
  variant?: "compact" | "preview";
  cols?: number | null;
}

export function CardSelectField({
  name,
  label,
  items,
  defaultValue,
  tooltip,
  isDisabled,
  variant = "compact",
  cols,
}: CardSelectFieldProps) {
  const { control } = useFormContext();
  const { field } = useController({ name, control, defaultValue });

  return (
    <div className="flex flex-col gap-2">
      <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
      <div
        className="grid gap-3"
        style={
          cols != null
            ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }
            : { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
        }
      >
        {items.map((item) => {
          const isSelected = field.value === item.value;

          if (variant === "preview") {
            return (
              <button
                key={item.value}
                type="button"
                disabled={isDisabled}
                onClick={() => field.onChange(item.value)}
                className={cn(
                  "flex flex-col gap-2.5 rounded-xl border p-3 text-left transition-all cursor-pointer",
                  "hover:border-foreground/30",
                  isSelected
                    ? "border-foreground ring-1 ring-foreground"
                    : "border-border bg-background",
                  isDisabled && "cursor-not-allowed opacity-50"
                )}
              >
                {/* Preview area */}
                {item.image && (
                  <div
                    className="w-full rounded-lg bg-muted/60 overflow-hidden flex items-center justify-center"
                    style={{ aspectRatio: "16/9", minHeight: "72px" }}
                  >
                    <img
                      src={item.image}
                      alt={item.label}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {/* Label + description */}
                <div className="flex flex-col gap-0.5 px-0.5">
                  <span className="text-sm font-medium leading-snug">
                    {item.label}
                  </span>
                  {item.description && (
                    <span className="text-xs text-muted-foreground leading-snug">
                      {item.description}
                    </span>
                  )}
                </div>
              </button>
            );
          }

          // compact variant (default)
          return (
            <button
              key={item.value}
              type="button"
              disabled={isDisabled}
              onClick={() => field.onChange(item.value)}
              className={cn(
                "flex flex-col gap-1 rounded-md border p-3 text-left transition-colors",
                "hover:border-primary/60 hover:bg-accent",
                isSelected
                  ? "border-primary bg-accent"
                  : "border-border bg-background",
                isDisabled && "cursor-not-allowed opacity-50"
              )}
            >
              <span className="text-sm font-medium leading-none">
                {item.label}
              </span>
              {item.description && (
                <span className="text-xs text-muted-foreground leading-snug">
                  {item.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
